import { array, CommunicationStatus, compactFlatten, isFailure, isMissing, isSuccess, success } from '@art-suite/art-core-ts';
import { useMemo } from 'react';

import { CachedQueryStatus } from './types';

/***********************************************************************
 * Helper functions for derived statuses
 ***********************************************************************/
const getCombinedStatus = (statusInfos: CachedQueryStatus[]): CommunicationStatus => {
  let lastErrorStatus: CommunicationStatus | undefined;
  let lastNonErrorNonSuccessStatus: CommunicationStatus | undefined;
  for (const statusInfo of statusInfos) {
    if (isSuccess(statusInfo.status)) continue;

    if (isFailure(statusInfo.status)) {
      lastErrorStatus = statusInfo.status;
    } else {
      lastNonErrorNonSuccessStatus = statusInfo.status;
    }
  }
  return lastErrorStatus ?? lastNonErrorNonSuccessStatus ?? success;
};

const getCombinedProgress = (statusInfos: CachedQueryStatus[]) => {
  if (statusInfos.length === 0) return 0;
  const sumProgress = statusInfos.reduce((a, b) => a + b.progress, 0);
  return sumProgress / statusInfos.length;
};

const getCombinedRefetch = (statusInfos: CachedQueryStatus[]) => {
  return () => Promise.all(statusInfos.map(s => s.refetch())).then(compactFlatten).then(() => undefined);
};

const getCombinedCachedQueryStatusCore = (statusInfos: CachedQueryStatus[]) => {
  let lastError: Error | undefined;
  let foundNotReady = false;
  let foundNotEnabled = false;
  let foundIsFetching = false;
  let errorInfoArray: string[] | undefined = undefined;
  for (const statusInfo of statusInfos) {
    if (!statusInfo.isReady) foundNotReady = true;
    if (!statusInfo.isEnabled) foundNotEnabled = true;
    if (statusInfo.isFetching) foundIsFetching = true;
    if (statusInfo.error) {
      lastError = statusInfo.error;
    }
    if (statusInfo.errorInfo) {
      errorInfoArray = errorInfoArray ?? [];
      errorInfoArray.push(statusInfo.errorInfo);
    }
  }
  const isReady = !foundNotReady,
    isFetching = foundIsFetching,
    isEnabled = !foundNotEnabled,
    status = getCombinedStatus(statusInfos),
    progress = getCombinedProgress(statusInfos),
    error = lastError,
    errorInfo = errorInfoArray ? errorInfoArray.join(', ') : undefined;

  return {
    error,
    errorInfo,
    progress,
    ...getDerivedStatus(isReady, isFetching, isEnabled, status),
  };
};

export const getCombinedCachedQueryStatusCoreWithRefetch = (statusInfos: CachedQueryStatus[]): CachedQueryStatus => {
  return {
    ...getCombinedCachedQueryStatusCore(statusInfos),
    refetch: getCombinedRefetch(statusInfos),
  };
};

/***********************************************************************
 * Derived statuses
 ***********************************************************************/
export const getDerivedStatus = (isReady: boolean, isFetching: boolean, isEnabled: boolean, status: CommunicationStatus) => {
  return {
    isReady,
    isFetching,
    isEnabled,
    status,
    isLoading: !isReady && isFetching,
    isRefetching: isReady && isFetching,
    isSuccess: isSuccess(status),
    isError: isReady ? false : isFailure(status),
    isMissing: isMissing(status),
  };
};

/***********************************************************************
 * Combined statuses
 ***********************************************************************/

export const getCombinedCachedQueryStatus = (...statusInfos: CachedQueryStatus[]): CachedQueryStatus =>
  getCombinedCachedQueryStatusCoreWithRefetch(statusInfos);

export const useCombinedCachedQueryStatus = (...statusInfos: CachedQueryStatus[]): CachedQueryStatus => {
  return {
    ...getCombinedCachedQueryStatusCore(statusInfos),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refetch: useMemo(() => getCombinedRefetch(statusInfos), statusInfos.map(s => s.refetch)),
  };
};

export const getCombinedCachedQueryStatusNamed = (statusInfos: Record<string, CachedQueryStatus>): CachedQueryStatus => {
  const base = getCombinedCachedQueryStatusCoreWithRefetch(Object.values(statusInfos));
  if (base.errorInfo) {
    base.errorInfo = array(statusInfos, {
      when: v => v.error,
      with: (v: CachedQueryStatus, k: string) => k + ': ' + v.errorInfo,
    }).join(', ');
  }
  return base;
};
